import Header from "../Header";
import { GenderType } from "../../utils/enums";
import { directionMessages, FACE_SCAN_HEADSHOT, glassesOffVideo, maleGlassesOffVideo } from "../../utils/constants";
import { LanguageContext } from "../../utils/context/languageContext";
import { useContext } from "react";
import SpecificButton from "../../atoms/specificButton/SpecificButton";
import { LanguageKeys } from "../../utils/languageKeys";
import { useConfig } from "../../utils/context/configContext";
import { Dispatch, ReactNode, SetStateAction } from "react";
import { Gender } from "../../types/interfaces";

interface IButtonConfig {
	isDisabled: boolean;
	icon?: ReactNode;
	onClick: () => void;
	label: string;
}

interface FaceScanGuideProps {
	stage: number;
	videoLoading: boolean;
	setVideoLoading: Dispatch<SetStateAction<boolean>>;
	videoError: boolean;
	setVideoError: Dispatch<SetStateAction<boolean>>;
	gender: Gender;
	btnConfig: IButtonConfig;
}

function FaceScanGuide({ stage, videoLoading, setVideoLoading, videoError, setVideoError, gender, btnConfig }: FaceScanGuideProps) {
	const { translate } = useContext(LanguageContext) || {};
	const config = useConfig();

	if (stage === -1) {
		return (
			<div data-testid="face-scan-guide" className="text-center common-ui-main  p-[16px] w-full h-full" style={{ background: config?.style?.base?.backgroundColor }}>
				<div className="w-full">
					<Header noTitle />
					<h2
						style={{
							fontFamily: config?.style?.heading?.headingFontFamily || "SeriouslyNostalgic Fn",
							fontSize: config?.style?.heading?.headingFontSize || "32px",
							color: config?.style?.heading?.headingColor || "#000",
							fontWeight: config?.style?.heading?.headingFontWeight || "normal",
						}}
					>
						{translate?.(LanguageKeys.faceVisible)}
					</h2>
					{videoLoading && (
						<div
							className="mb-4 flex items-center justify-center"
							style={{
								fontFamily: config?.style?.subheading?.subheadingFontFamily || "'Inter', sans-serif",
								fontSize: config?.style?.subheading?.subheadingFontSize || "14px",
								color: config?.style?.subheading?.subheadingColor || "#4b5563",
								fontWeight: config?.style?.subheading?.subheadingFontWeight || "normal",
							}}
						>
							{translate?.(LanguageKeys.loadingVideo)}
						</div>
					)}
					{videoError && (
						<div
							className="mb-4 text-red-600"
							style={{
								fontFamily: config?.style?.subheading?.subheadingFontFamily || "'Inter', sans-serif",
								fontSize: config?.style?.subheading?.subheadingFontSize || "14px",
								color: "#ff0000",
								fontWeight: config?.style?.subheading?.subheadingFontWeight || "normal",
							}}
						>
							{translate?.(LanguageKeys.videoLoadFailed)}
						</div>
					)}
					{!videoError && (
						<div className={` w-[100px] mx-auto`}>
							<video
								src={gender === GenderType.Male ? maleGlassesOffVideo : glassesOffVideo}
								autoPlay
								loop
								controls={false}
								muted
								playsInline
								className="h-full w-full object-contain border-none"
								onCanPlay={() => setVideoLoading(false)}
								onLoadStart={() => setVideoLoading(true)}
								onError={() => setVideoError(true)}
								aria-label="Instructional video: Please remove your glasses before starting the scan."
							/>
						</div>
					)}
				</div>
				<div className="flex justify-center w-full  p-[1rem]">
					<SpecificButton
						data-testid="face-scan-start-btn"
						disabled={btnConfig.isDisabled}
						className="!w-[60px] !h-[35px] !py-0 !px-0"
						buttonText={translate?.(btnConfig.label)}
						postfixIcon={btnConfig?.icon}
						buttonFunc={btnConfig.onClick}
					/>
				</div>
			</div>
		);
	}

	return (
		<div className="text-center common-ui-main  p-[16px] w-full h-full" style={{ background: config?.style?.base?.backgroundColor }}>
			<div className="w-full">
				<Header noTitle />
				<h3
					className="mb-[0.5rem]"
					style={{
						fontFamily: config?.style?.heading?.headingFontFamily || "SeriouslyNostalgic Fn",
						fontSize: config?.style?.heading?.headingFontSize || "32px",
						color: config?.style?.heading?.headingColor || "#000",
						fontWeight: config?.style?.heading?.headingFontWeight || "normal",
					}}
				>
					{translate?.(directionMessages[stage])}
				</h3>
				<div className="max-w-[400px] justify-center gap-1 mx-auto flex items-center">
					<div className={` w-[120px] overflow-hidden ${stage === 0 ? "opacity-100" : "opacity-0 hidden"} `}>
						<video className="h-full w-full object-contain border-none" muted loop autoPlay playsInline>
							<source src={gender === GenderType.Male ? FACE_SCAN_HEADSHOT.male.forward : FACE_SCAN_HEADSHOT.female.forward} type="video/mp4" />
						</video>
					</div>
					<div className={` w-[120px] overflow-hidden ${stage === 1 ? "opacity-100" : "opacity-0 hidden"} `}>
						<video className="h-full w-full object-contain border-none" muted loop autoPlay playsInline>
							<source src={gender === GenderType.Male ? FACE_SCAN_HEADSHOT.male.left : FACE_SCAN_HEADSHOT.female.left} type="video/mp4" />
						</video>
					</div>
					<div className={` w-[120px] overflow-hidden ${stage === 2 ? "opacity-100" : "opacity-0 hidden"} `}>
						<video className="h-full w-full object-contain border-none" muted loop autoPlay playsInline>
							<source src={gender === GenderType.Male ? FACE_SCAN_HEADSHOT.male.right : FACE_SCAN_HEADSHOT.female.right} type="video/mp4" />
						</video>
					</div>
					<div className={` w-[120px] overflow-hidden ${stage === 3 ? "opacity-100" : "opacity-0 hidden"} `}>
						<video className="h-full w-full object-contain border-none" muted loop autoPlay playsInline>
							<source src={gender === GenderType.Male ? FACE_SCAN_HEADSHOT.male.smile : FACE_SCAN_HEADSHOT.female.smile} type="video/mp4" />
						</video>
					</div>
				</div>
			</div>
		</div>
	);
}

export default FaceScanGuide;
